import { useEffect, useRef, useState } from 'react';

// The whiteboard, which exists to end a device dance.
//
// Most of a second assessment meeting happens on one: the cash flow diagram,
// the tax triangle, the bracket ladder. Today the advisor shares their desktop
// for the deck, switches the share to an iPad to draw in OneNote, then
// switches back. The ask, verbatim: "if we were able to say, okay, after this
// slide it pulls up a whiteboard and now the advisor can draw on that." So the
// board is a page in the flow, and pointer events mean one page takes a mouse,
// a finger, or the iPad pencil.
//
// WHAT IS SAVED IS THE STROKES, NOT THE PICTURE, and the reason is arithmetic.
// A realistic forty-stroke sketch is 375,678 characters as a PNG data URL and
// 25,750 as a stroke list. Salesforce's Long Text Area holds 131,072, so the
// image cannot live on the household's record at all while the stroke list
// uses a fifth of one field. The first board saved to Salesforce through the
// old format, one stroke, was 108,085 characters: 82% of the field for a
// single line. Two things fall out of strokes for free: the drawing prints at
// the printer's resolution instead of as a soft 1568px bitmap, and what we keep
// is legible data rather than an opaque blob.
//
// Boards drawn before this change exist as PNG data URLs, on advisors' devices
// and in at least one Salesforce record. They are not thrown away and not
// hidden: a legacy picture is shown under the ink layer, kept with the sketch
// if the advisor draws over it, and only dropped when the advisor clears the
// board on purpose. Such a board stays too large for Salesforce until then, and
// the page says so.
//
// Coordinates live in a FIXED logical space, scaled by CSS. That keeps a
// drawing honest when the window resizes mid-meeting and makes it replay
// identically on every screen that later opens it.

export const BOARD_W = 1568;
export const BOARD_H = 760;
export const SKETCH_VERSION = 1;
const SAVE_DELAY_MS = 900;

const MARKERS = [
  { name: 'Ink', color: '#1a2040' },
  { name: 'Green', color: '#1a7a4a' },
  { name: 'Blue', color: '#015697' },
  { name: 'Brick', color: '#c0392b' },
];

/** A board saved by the old code: the whole picture as a PNG data URL. */
export const isLegacyImage = saved => typeof saved === 'string' && saved.startsWith('data:image/');

/** The strokes of a saved sketch, or none. */
export const strokesOf = saved =>
  saved && typeof saved === 'object' && Array.isArray(saved.strokes) ? saved.strokes : [];

/** The legacy picture a sketch carries, if any: the old format itself, or one kept under new ink. */
export const legacyImageOf = saved => {
  if (isLegacyImage(saved)) return saved;
  return saved && typeof saved === 'object' && isLegacyImage(saved.legacy) ? saved.legacy : null;
};

/** Whether there is anything on the board at all. */
export const hasInk = saved => strokesOf(saved).length > 0 || Boolean(legacyImageOf(saved));

/**
 * Replay a sketch's strokes onto a context. The canvas is CLEARED, not painted
 * white: it is an ink layer over whatever the board is ruled with, so the guide
 * or a legacy picture underneath shows through and the strokes sit on top.
 */
export function drawSketch(c, sketch) {
  c.clearRect(0, 0, BOARD_W, BOARD_H);
  if (!sketch || !Array.isArray(sketch.strokes)) return;
  c.lineCap = 'round';
  c.lineJoin = 'round';
  sketch.strokes.forEach(stroke => {
    const pts = stroke && stroke.p;
    if (!Array.isArray(pts) || !pts.length) return;
    c.strokeStyle = stroke.c || '#1a2040';
    c.lineWidth = stroke.w || 3.5;
    c.beginPath();
    c.moveTo(pts[0][0], pts[0][1]);
    // A single tap is a dot, not a zero-length path that renders nothing.
    if (pts.length === 1) c.lineTo(pts[0][0] + 0.01, pts[0][1] + 0.01);
    for (let i = 1; i < pts.length; i += 1) c.lineTo(pts[i][0], pts[i][1]);
    c.stroke();
  });
}

// `guide` is an SVG <g> drawn under the ink: the household's own cash-flow
// timeline, or nothing for a blank board. `background` is a legacy picture,
// shown under the ink instead of a guide. `saveWith` is what travels with the
// strokes when they are saved (the guide's figures, the legacy picture), so a
// drawing can never be quietly reinterpreted. `note` is a line shown under the
// board, for the two cases where the advisor has to be told something.
export default function Whiteboard({
  saved,
  onSave,
  ariaLabel,
  guide = null,
  background = null,
  saveWith = null,
  note = null,
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const saveTimer = useRef(null);
  const strokes = useRef([]);
  const current = useRef(null);
  const [marker, setMarker] = useState(MARKERS[0].color);
  const [hasStrokes, setHasStrokes] = useState(strokesOf(saved).length > 0);
  const saveWithRef = useRef(saveWith);
  saveWithRef.current = saveWith;

  const ctx = () => canvasRef.current.getContext('2d');

  // Seeding watches `saved` rather than running once, because with ?leadId=
  // the store hydrates from Salesforce AFTER this mounts: a mount-only seed
  // leaves the advisor looking at a blank board with their own work behind it.
  // `touched` is a ref, not state, so drawing never re-triggers a seed and
  // wipes the line in progress.
  const seeded = useRef(null);
  const touched = useRef(false);

  useEffect(() => {
    if (saved === seeded.current || touched.current) return;
    strokes.current = strokesOf(saved).slice();
    drawSketch(ctx(), { strokes: strokes.current });
    seeded.current = saved;
    setHasStrokes(strokes.current.length > 0);
  }, [saved]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const point = e => {
    const rect = canvasRef.current.getBoundingClientRect();
    // Whole pixels: half a pixel is invisible on a marker stroke and costs
    // real characters in a field with a hard ceiling.
    return [
      Math.round(((e.clientX - rect.left) / rect.width) * BOARD_W),
      Math.round(((e.clientY - rect.top) / rect.height) * BOARD_H),
    ];
  };

  const commit = () => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const extra = {};
      Object.entries(saveWithRef.current || {}).forEach(([k, v]) => {
        if (v != null) extra[k] = v;
      });
      const keep = strokes.current.length > 0 || Boolean(extra.legacy);
      const sketch = keep
        ? { v: SKETCH_VERSION, w: BOARD_W, h: BOARD_H, strokes: strokes.current, ...extra }
        : null;
      seeded.current = sketch;   // our own write is not a new drawing to seed from
      onSave(sketch);
    }, SAVE_DELAY_MS);
  };

  const down = e => {
    // A palm resting on an iPad arrives as a second pointer; one line at a time.
    if (drawing.current) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    touched.current = true;
    current.current = { c: marker, w: 3.5, p: [point(e)] };
    strokes.current = [...strokes.current, current.current];
    drawSketch(ctx(), { strokes: strokes.current });
  };

  const move = e => {
    if (!drawing.current) return;
    const [x, y] = point(e);
    const pts = current.current.p;
    const last = pts[pts.length - 1];
    // Drop points the eye cannot resolve. Pointer events fire far faster than
    // a marker changes direction, and every kept point is characters.
    if (Math.abs(x - last[0]) < 2 && Math.abs(y - last[1]) < 2) return;
    pts.push([x, y]);
    const c = ctx();
    c.strokeStyle = current.current.c;
    c.lineWidth = current.current.w;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    c.beginPath();
    c.moveTo(last[0], last[1]);
    c.lineTo(x, y);
    c.stroke();
  };

  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    current.current = null;
    setHasStrokes(strokes.current.length > 0);
    commit();
  };

  // Undo drops the last stroke and replays, rather than restoring a bitmap. It
  // undoes what the advisor thinks of as one action, and a legacy picture is
  // not a stroke, so it is never undone into oblivion by accident.
  const undo = () => {
    if (!strokes.current.length) return;
    strokes.current = strokes.current.slice(0, -1);
    drawSketch(ctx(), { strokes: strokes.current });
    setHasStrokes(strokes.current.length > 0);
    commit();
  };

  // Clear is the one deliberate act that drops everything, the legacy picture
  // included, so it asks first when there is something to lose. It saves at
  // once rather than after the debounce: nothing on the board means nothing
  // saved, and the board's ruling returns to today's figures.
  const clear = () => {
    if (strokes.current.length || background) {
      const what = background && !strokes.current.length ? 'this picture' : 'this board';
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Clear ${what}? This cannot be undone.`)) return;
    }
    clearTimeout(saveTimer.current);
    strokes.current = [];
    drawSketch(ctx(), null);
    setHasStrokes(false);
    seeded.current = null;
    onSave(null);
  };

  return (
    <div className="asmt-board-wrap">
      <div className="asmt-board-tools no-print">
        {MARKERS.map(m => (
          <button
            key={m.color}
            type="button"
            className={`asmt-board-marker${marker === m.color ? ' is-active' : ''}`}
            style={{ background: m.color }}
            aria-label={`${m.name} marker`}
            aria-pressed={marker === m.color}
            onClick={() => setMarker(m.color)}
          />
        ))}
        <span className="asmt-board-spacer" />
        <button type="button" className="asmt-board-act" onClick={undo} disabled={!hasStrokes}>Undo</button>
        <button type="button" className="asmt-board-act" onClick={clear}>Clear</button>
      </div>
      <div className="asmt-board-stack">
        {background && (
          <img className="asmt-board-legacy" src={background} alt="" aria-hidden="true" />
        )}
        {guide && !background && (
          <svg className="asmt-board-guide" viewBox={`0 0 ${BOARD_W} ${BOARD_H}`} aria-hidden="true">
            {guide}
          </svg>
        )}
        <canvas
          ref={canvasRef}
          className="asmt-board"
          width={BOARD_W}
          height={BOARD_H}
          aria-label={ariaLabel}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
        />
      </div>
      {note && <p className="asmt-board-note no-print">{note}</p>}
    </div>
  );
}
